import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { 
  Calendar as CalendarIcon, Clock, User, Plus, ChevronLeft, ChevronRight, 
  Check, X, Grid3X3, List, Users, Armchair
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  scheduled: 'bg-blue-500 hover:bg-blue-600 border-blue-600',
  confirmed: 'bg-green-500 hover:bg-green-600 border-green-600',
  checked_in: 'bg-yellow-500 hover:bg-yellow-600 border-yellow-600',
  in_progress: 'bg-purple-500 hover:bg-purple-600 border-purple-600',
  completed: 'bg-slate-400 hover:bg-slate-500 border-slate-500',
  cancelled: 'bg-red-400 hover:bg-red-500 border-red-500',
  no_show: 'bg-orange-400 hover:bg-orange-500 border-orange-500'
};

const STATUS_LABELS = {
  scheduled: 'Scheduled',
  confirmed: 'Confirmed',
  checked_in: 'Checked In',
  in_progress: 'In Progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
  no_show: 'No Show'
};

// Chairs/Operatories configuration
const CHAIRS = ['1', '2', '3', '4'];

// Time slots from 7 AM to 7 PM in 30-minute increments
const generateTimeSlots = () => {
  const slots = [];
  for (let hour = 7; hour <= 19; hour++) {
    slots.push(`${hour.toString().padStart(2, '0')}:00`);
    slots.push(`${hour.toString().padStart(2, '0')}:30`);
  }
  return slots;
};

const TIME_SLOTS = generateTimeSlots();

const DentalScheduling = () => {
  const { token, user } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [weekAppointments, setWeekAppointments] = useState([]);
  const [patients, setPatients] = useState([]);
  const [providers, setProviders] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('day'); // 'day', 'week', 'list'
  const [showModal, setShowModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showCalendarPicker, setShowCalendarPicker] = useState(false);
  const [formData, setFormData] = useState({
    patient_id: '',
    provider_id: '',
    service_ids: [],
    scheduled_date: '',
    scheduled_time: '',
    duration_minutes: 30,
    notes: '',
    chair_number: '1'
  });

  const dateStr = selectedDate.toISOString().split('T')[0];

  // Get week dates for week view
  const getWeekDates = (date) => {
    const week = [];
    const start = new Date(date);
    start.setDate(start.getDate() - start.getDay()); // Start from Sunday
    for (let i = 0; i < 7; i++) {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      week.push(d);
    }
    return week;
  };

  const weekDates = useMemo(() => getWeekDates(selectedDate), [selectedDate]);

  useEffect(() => {
    fetchData();
  }, [dateStr, viewMode]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const requests = [
        axios.get(`${API_URL}/api/dental/appointments?date=${dateStr}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/patients`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/users`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/dental/services`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ];

      // If week view, fetch appointments for the whole week
      if (viewMode === 'week') {
        const weekStart = weekDates[0].toISOString().split('T')[0];
        const weekEnd = weekDates[6].toISOString().split('T')[0];
        requests.push(
          axios.get(`${API_URL}/api/dental/appointments?start_date=${weekStart}&end_date=${weekEnd}`, {
            headers: { Authorization: `Bearer ${token}` }
          }).catch(() => ({ data: [] }))
        );
      }

      const responses = await Promise.all(requests);
      setAppointments(responses[0].data);
      setPatients(responses[1].data);
      setProviders(responses[2].data.filter(u => 
        ['owner', 'dentist', 'hygienist', 'doctor'].includes(u.role)
      ));
      setServices(responses[3].data);
      
      if (viewMode === 'week' && responses[4]) {
        setWeekAppointments(responses[4].data);
      }
    } catch (error) {
      toast.error('Failed to load scheduling data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAppointment = async (e) => {
    e.preventDefault();
    try {
      if (selectedAppointment) {
        // Update existing
        await axios.put(
          `${API_URL}/api/dental/appointments/${selectedAppointment.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Appointment updated');
      } else {
        // Create new
        await axios.post(`${API_URL}/api/dental/appointments`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Appointment scheduled');
      }
      setShowModal(false);
      setSelectedAppointment(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save appointment');
    }
  };

  const updateAppointmentStatus = async (appointmentId, status) => {
    try {
      await axios.put(
        `${API_URL}/api/dental/appointments/${appointmentId}?status=${status}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Appointment ${status.replace('_', ' ')}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update appointment');
    }
  };

  const navigateDate = (days) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + days);
    setSelectedDate(newDate);
  };

  const navigateWeek = (weeks) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + (weeks * 7));
    setSelectedDate(newDate);
  };

  const openCreateModal = (time = '', chair = '1', date = null) => {
    const targetDate = date || selectedDate;
    setSelectedAppointment(null);
    setFormData({
      patient_id: '',
      provider_id: user?.id || '',
      service_ids: [],
      scheduled_date: targetDate.toISOString().split('T')[0],
      scheduled_time: time,
      duration_minutes: 30,
      notes: '',
      chair_number: chair
    });
    setShowModal(true);
  };

  const openEditModal = (appointment) => {
    setSelectedAppointment(appointment);
    setFormData({
      patient_id: appointment.patient_id,
      provider_id: appointment.provider_id,
      service_ids: appointment.service_ids || [],
      scheduled_date: appointment.scheduled_date,
      scheduled_time: appointment.scheduled_time,
      duration_minutes: appointment.duration_minutes,
      notes: appointment.notes || '',
      chair_number: appointment.chair_number || '1'
    });
    setShowModal(true);
  };

  const getAppointmentForSlot = (time, chair, date = null) => {
    const targetDate = date ? date.toISOString().split('T')[0] : dateStr;
    const apptList = date ? weekAppointments : appointments;
    return apptList.find(a => 
      a.scheduled_time === time && 
      a.chair_number === chair &&
      a.scheduled_date === targetDate
    );
  };

  const getAppointmentHeight = (duration) => {
    // Each 30 min slot is 48px
    return (duration / 30) * 48;
  };

  // Day View Component
  const DayView = () => (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      {/* Chair Headers */}
      <div className="grid grid-cols-5 border-b bg-slate-50">
        <div className="p-3 font-medium text-slate-600 border-r">Time</div>
        {CHAIRS.map(chair => (
          <div key={chair} className="p-3 font-medium text-slate-700 text-center border-r last:border-r-0">
            <div className="flex items-center justify-center gap-2">
              <Armchair className="w-4 h-4" />
              Chair {chair}
            </div>
          </div>
        ))}
      </div>

      {/* Time Grid */}
      <div className="max-h-[600px] overflow-y-auto">
        {TIME_SLOTS.map((time, idx) => (
          <div key={time} className={`grid grid-cols-5 ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}>
            <div className="p-2 text-sm text-slate-500 border-r border-b font-mono">
              {time}
            </div>
            {CHAIRS.map(chair => {
              const appointment = getAppointmentForSlot(time, chair);
              return (
                <div
                  key={`${time}-${chair}`}
                  className="relative border-r border-b last:border-r-0 h-12 cursor-pointer hover:bg-blue-50 transition-colors"
                  onClick={() => !appointment && openCreateModal(time, chair)}
                >
                  {appointment && (
                    <div
                      className={`absolute inset-x-1 top-1 rounded-md p-1 text-white text-xs cursor-pointer z-10 border-l-4 ${STATUS_COLORS[appointment.status]}`}
                      style={{ 
                        height: `${getAppointmentHeight(appointment.duration_minutes) - 8}px`,
                        minHeight: '38px'
                      }}
                      onClick={(e) => { e.stopPropagation(); openEditModal(appointment); }}
                    >
                      <div className="font-medium truncate">{appointment.patient_name}</div>
                      <div className="truncate opacity-90">
                        {appointment.services?.[0]?.service_name || 'Appointment'}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );

  // Week View Component
  const WeekView = () => (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      {/* Day Headers */}
      <div className="grid grid-cols-8 border-b bg-slate-50">
        <div className="p-3 font-medium text-slate-600 border-r">Time</div>
        {weekDates.map(date => {
          const isToday = date.toDateString() === new Date().toDateString();
          const isSelected = date.toDateString() === selectedDate.toDateString();
          return (
            <div 
              key={date.toISOString()} 
              className={`p-2 text-center border-r last:border-r-0 cursor-pointer hover:bg-blue-50 ${isToday ? 'bg-blue-100' : ''} ${isSelected ? 'ring-2 ring-blue-500 ring-inset' : ''}`}
              onClick={() => setSelectedDate(date)}
            >
              <div className="text-xs text-slate-500">{date.toLocaleDateString('en-US', { weekday: 'short' })}</div>
              <div className={`text-lg font-semibold ${isToday ? 'text-blue-600' : 'text-slate-700'}`}>
                {date.getDate()}
              </div>
            </div>
          );
        })}
      </div>

      {/* Time Grid */}
      <div className="max-h-[500px] overflow-y-auto">
        {TIME_SLOTS.filter((_, i) => i % 2 === 0).map((time, idx) => (
          <div key={time} className={`grid grid-cols-8 ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}>
            <div className="p-2 text-xs text-slate-500 border-r border-b font-mono">
              {time}
            </div>
            {weekDates.map(date => {
              const dayAppts = weekAppointments.filter(a => 
                a.scheduled_date === date.toISOString().split('T')[0] &&
                a.scheduled_time >= time &&
                a.scheduled_time < TIME_SLOTS[TIME_SLOTS.indexOf(time) + 2]
              );
              return (
                <div
                  key={`${time}-${date.toISOString()}`}
                  className="relative border-r border-b last:border-r-0 h-16 cursor-pointer hover:bg-blue-50 transition-colors p-0.5"
                  onClick={() => openCreateModal(time, '1', date)}
                >
                  {dayAppts.slice(0, 2).map((appointment, i) => (
                    <div
                      key={appointment.id}
                      className={`rounded text-white text-[10px] p-1 mb-0.5 cursor-pointer truncate ${STATUS_COLORS[appointment.status]}`}
                      onClick={(e) => { e.stopPropagation(); openEditModal(appointment); }}
                    >
                      {appointment.scheduled_time} {appointment.patient_name?.split(',')[0]}
                    </div>
                  ))}
                  {dayAppts.length > 2 && (
                    <div className="text-[10px] text-slate-500">+{dayAppts.length - 2} more</div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );

  // List View Component
  const ListView = () => (
    <div className="bg-white rounded-lg shadow">
      <div className="p-4 border-b">
        <h2 className="font-semibold text-slate-800">
          {selectedDate.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </h2>
        <p className="text-sm text-slate-500">{appointments.length} appointments</p>
      </div>

      {appointments.length === 0 ? (
        <div className="p-8 text-center text-slate-500">
          <CalendarIcon className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <p>No appointments scheduled for this date</p>
          <Button className="mt-4" onClick={() => openCreateModal()}>
            <Plus className="w-4 h-4 mr-2" />
            Schedule Appointment
          </Button>
        </div>
      ) : (
        <div className="divide-y">
          {appointments.sort((a, b) => a.scheduled_time.localeCompare(b.scheduled_time)).map(appt => (
            <div key={appt.id} className="p-4 hover:bg-slate-50 transition-colors">
              <div className="flex justify-between items-start">
                <div className="flex gap-4">
                  <div className="text-center min-w-[70px]">
                    <div className="text-xl font-bold text-slate-800">{appt.scheduled_time}</div>
                    <div className="text-xs text-slate-500">{appt.duration_minutes} min</div>
                    {appt.chair_number && (
                      <div className="text-xs text-slate-400 flex items-center justify-center gap-1 mt-1">
                        <Armchair className="w-3 h-3" />
                        Chair {appt.chair_number}
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="font-medium text-slate-800 text-lg">{appt.patient_name}</div>
                    <div className="text-sm text-slate-500 flex items-center gap-1">
                      <User className="w-3 h-3" />
                      {appt.provider_name}
                    </div>
                    {appt.services && appt.services.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {appt.services.map(svc => (
                          <span key={svc.id} className="text-xs bg-teal-50 text-teal-700 px-2 py-0.5 rounded">
                            {svc.service_name}
                          </span>
                        ))}
                      </div>
                    )}
                    {appt.notes && (
                      <p className="text-xs text-slate-400 mt-2 italic">{appt.notes}</p>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium text-white ${STATUS_COLORS[appt.status]}`}>
                    {STATUS_LABELS[appt.status]}
                  </span>
                  <div className="flex gap-1">
                    {appt.status === 'scheduled' && (
                      <>
                        <Button size="sm" variant="outline" className="text-green-600 h-8" onClick={() => updateAppointmentStatus(appt.id, 'confirmed')}>
                          <Check className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-600 h-8" onClick={() => updateAppointmentStatus(appt.id, 'cancelled')}>
                          <X className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                    {appt.status === 'confirmed' && (
                      <Button size="sm" onClick={() => updateAppointmentStatus(appt.id, 'checked_in')}>
                        Check In
                      </Button>
                    )}
                    {appt.status === 'checked_in' && (
                      <Button size="sm" onClick={() => updateAppointmentStatus(appt.id, 'in_progress')}>
                        Start
                      </Button>
                    )}
                    {appt.status === 'in_progress' && (
                      <Button size="sm" onClick={() => updateAppointmentStatus(appt.id, 'completed')}>
                        Complete
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => openEditModal(appt)}>
                      Edit
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  if (loading && appointments.length === 0) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6" data-testid="dental-scheduling">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Dental Scheduling</h1>
            <p className="text-slate-500">Manage appointments and chair assignments</p>
          </div>
          <Button onClick={() => openCreateModal()} className="bg-teal-600 hover:bg-teal-700" data-testid="new-appointment-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Appointment
          </Button>
        </div>

        {/* Controls */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          {/* Date Navigation */}
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="icon" 
              onClick={() => viewMode === 'week' ? navigateWeek(-1) : navigateDate(-1)}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            
            <div className="relative">
              <Button 
                variant="outline" 
                className="min-w-[200px]"
                onClick={() => setShowCalendarPicker(!showCalendarPicker)}
              >
                <CalendarIcon className="w-4 h-4 mr-2" />
                {viewMode === 'week' 
                  ? `${weekDates[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekDates[6].toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                  : selectedDate.toLocaleDateString('en-US', { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric' })
                }
              </Button>
              {showCalendarPicker && (
                <div className="absolute top-full left-0 mt-2 z-50 bg-white rounded-lg shadow-lg border">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(date) => { if(date) { setSelectedDate(date); setShowCalendarPicker(false); }}}
                  />
                </div>
              )}
            </div>

            <Button 
              variant="outline" 
              size="icon" 
              onClick={() => viewMode === 'week' ? navigateWeek(1) : navigateDate(1)}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>

            <Button 
              variant="outline"
              onClick={() => setSelectedDate(new Date())}
            >
              Today
            </Button>
          </div>

          {/* View Toggle */}
          <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-lg">
            <Button 
              size="sm"
              variant={viewMode === 'day' ? 'default' : 'ghost'}
              className={viewMode === 'day' ? 'bg-white shadow' : ''}
              onClick={() => setViewMode('day')}
            >
              <Grid3X3 className="w-4 h-4 mr-1" />
              Day
            </Button>
            <Button 
              size="sm"
              variant={viewMode === 'week' ? 'default' : 'ghost'}
              className={viewMode === 'week' ? 'bg-white shadow' : ''}
              onClick={() => setViewMode('week')}
            >
              <CalendarIcon className="w-4 h-4 mr-1" />
              Week
            </Button>
            <Button 
              size="sm"
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              className={viewMode === 'list' ? 'bg-white shadow' : ''}
              onClick={() => setViewMode('list')}
            >
              <List className="w-4 h-4 mr-1" />
              List
            </Button>
          </div>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-3 mb-4">
          {Object.entries(STATUS_LABELS).map(([key, label]) => (
            <div key={key} className="flex items-center gap-1 text-xs">
              <div className={`w-3 h-3 rounded ${STATUS_COLORS[key]}`}></div>
              <span className="text-slate-600">{label}</span>
            </div>
          ))}
        </div>

        {/* Calendar View */}
        {viewMode === 'day' && <DayView />}
        {viewMode === 'week' && <WeekView />}
        {viewMode === 'list' && <ListView />}

        {/* Create/Edit Appointment Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {selectedAppointment ? 'Edit Appointment' : 'Schedule Dental Appointment'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateAppointment} className="space-y-4">
              <div>
                <Label>Patient *</Label>
                <Select 
                  value={formData.patient_id} 
                  onValueChange={(v) => setFormData({...formData, patient_id: v})}
                >
                  <SelectTrigger data-testid="patient-select">
                    <SelectValue placeholder="Select patient" />
                  </SelectTrigger>
                  <SelectContent>
                    {patients.map(p => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.last_name}, {p.first_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Provider</Label>
                  <Select 
                    value={formData.provider_id} 
                    onValueChange={(v) => setFormData({...formData, provider_id: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select provider" />
                    </SelectTrigger>
                    <SelectContent>
                      {providers.map(p => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.full_name} ({p.role})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Chair/Operatory</Label>
                  <Select 
                    value={formData.chair_number} 
                    onValueChange={(v) => setFormData({...formData, chair_number: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select chair" />
                    </SelectTrigger>
                    <SelectContent>
                      {CHAIRS.map(c => (
                        <SelectItem key={c} value={c}>Chair {c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Date *</Label>
                  <Input
                    type="date"
                    value={formData.scheduled_date}
                    onChange={(e) => setFormData({...formData, scheduled_date: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label>Time *</Label>
                  <Select 
                    value={formData.scheduled_time} 
                    onValueChange={(v) => setFormData({...formData, scheduled_time: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select time" />
                    </SelectTrigger>
                    <SelectContent>
                      {TIME_SLOTS.map(time => (
                        <SelectItem key={time} value={time}>{time}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Service</Label>
                <Select 
                  value={formData.service_ids[0] || ''} 
                  onValueChange={(v) => {
                    const svc = services.find(s => s.id === v);
                    setFormData({
                      ...formData, 
                      service_ids: [v],
                      duration_minutes: svc?.duration_minutes || 30
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select service" />
                  </SelectTrigger>
                  <SelectContent>
                    {services.map(s => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.service_name} ({s.duration_minutes} min - J${s.default_price_jmd.toLocaleString()})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Duration (minutes)</Label>
                <Input
                  type="number"
                  value={formData.duration_minutes}
                  onChange={(e) => setFormData({...formData, duration_minutes: parseInt(e.target.value) || 30})}
                  min={15}
                  step={15}
                />
              </div>

              <div>
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Additional notes..."
                  rows={2}
                />
              </div>

              {selectedAppointment && (
                <div>
                  <Label>Status</Label>
                  <Select 
                    value={selectedAppointment.status}
                    onValueChange={(v) => updateAppointmentStatus(selectedAppointment.id, v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(STATUS_LABELS).map(([value, label]) => (
                        <SelectItem key={value} value={value}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="flex gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => { setShowModal(false); setSelectedAppointment(null); }} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-teal-600 hover:bg-teal-700">
                  {selectedAppointment ? 'Update' : 'Schedule'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default DentalScheduling;
